import { Controller, FieldValues, useFormState } from 'react-hook-form'
import { NumericFormat, NumberFormatValues } from 'react-number-format'

import { Group } from '~/components/component-library'
import { NumberInputProps } from '~/components/component-library/NumberInput/types'
import { ErrorFooter, visibleError } from '~/components/inputs/ErrorFooter'
import { Input } from '~/components/inputs/Input'
import { ALLOWED_DECIMAL_SEPARATORS } from '~/utils/numbers'

export const NumberInput = <FormType extends FieldValues = FieldValues>({
  control,
  inputName,
  placeholder,
  decimalScale,
  unit,
  suffix,
  prefix,
  optional = false,
  validationFunc = () => true,
  onClearInput,
  onChange,
  onKeyDown,
  autoFocus,
  onFocus,
  isLoading,
  label,
  description,
  tooltip,
  footer,
  className,
  backgroundShadow = true,
  focusRing = true,
  requiredErrorMessage = 'Required',
  size = 'base',
  min,
  max,
  ref,
  hideError,
  hideThousandSeparator,
  disabled,
  disabledReason,
}: NumberInputProps<FormType>) => {
  const { isSubmitted } = useFormState({ control })

  const validateBounds = (value: string): string | true => {
    if (!value) return true
    const num = Number(value)
    if (Number.isNaN(num)) return true
    if (min !== undefined && num < min) return `Must be at least ${min}`
    if (max !== undefined && num > max) return `Must be at most ${max}`
    return true
  }

  return (
    <Controller
      render={({ field, fieldState }) => {
        // hideError suppresses the error entirely — both the footer text and
        // the Input's red ring, which are both driven by `message`.
        const message = hideError
          ? undefined
          : visibleError(fieldState.error, {
              isTouched: fieldState.isTouched,
              isEmpty: !field.value,
              isSubmitted,
            })
        return (
          // TODO: should we just treat it as a string input instead? And convert to a number when necessary?
          <Group
            label={label}
            secondaryLabel={optional ? 'Optional' : undefined}
            description={description}
            tooltip={tooltip}
            footer={message ? <ErrorFooter message={message} /> : footer}
          >
            <NumericFormat
              onFocus={onFocus}
              type="text"
              customInput={Input}
              onValueChange={(values) => {
                field.onChange(values.value)
                if (onChange) onChange(values.value)
              }}
              onKeyDown={onKeyDown}
              required={!optional}
              onBlur={() => field.onBlur()}
              prefix={prefix}
              focusRing={focusRing}
              isAllowed={buildNumberFormatIsAllowed}
              allowedDecimalSeparators={ALLOWED_DECIMAL_SEPARATORS}
              thousandSeparator={!hideThousandSeparator}
              getInputRef={ref}
              backgroundShadow={backgroundShadow}
              className={className}
              rounded={true}
              decimalScale={decimalScale}
              suffix={suffix}
              unit={unit}
              placeholder={placeholder}
              value={field.value}
              autoFocus={autoFocus}
              onClearInput={onClearInput}
              clearEventName={'clearedNumberInput'}
              isLoading={isLoading}
              inputSize={size}
              // message drives Input's red ring; the text renders in the footer
              error={message}
              showError={false}
              disabled={disabled}
              disabledReason={disabledReason}
            />
          </Group>
        )
      }}
      name={inputName}
      control={control}
      rules={{
        required: {
          value: !optional,
          message: requiredErrorMessage,
        },
        validate: { bounds: validateBounds, custom: validationFunc },
      }}
    />
  )
}

export const buildNumberFormatIsAllowed = (values: NumberFormatValues) => {
  const { value } = values
  // Allow empty input
  if (!value) return true

  // Prevent leading zeros (except "0" by itself or "0." for decimals)
  // This regex matches strings that start with 0 followed by a digit (not a decimal point)
  if (/^0[0-9]/.test(value)) {
    return false
  }

  return true
}

NumberInput.displayName = 'NumberInput'

export type { NumberInputProps } from '~/components/component-library/NumberInput/types'
